import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qualityService, QualityPlan, QualityPlanItem, InspectionRecord, NCRRecord, CAPARecord } from '../services/quality.service';
import type { RejectionPayload } from '../services/rejection.service';
import { message } from 'antd';

      // Query keys
      export const qualityKeys = {
        all: ['quality'] as const,
        qualityPlans: (filters?: any) => [...qualityKeys.all, 'quality-plans', filters] as const,
        qualityPlan: (id: string) => [...qualityKeys.all, 'quality-plan', id] as const,
        qualityPlanItems: (planId: string) => [...qualityKeys.all, 'quality-plan-items', planId] as const,
        qualityPlanItem: (planId: string, itemId: string) => [...qualityKeys.all, 'quality-plan-item', planId, itemId] as const,
        pendingQualityPlanItems: () => [...qualityKeys.all, 'pending-quality-plan-items'] as const,
  inspections: (filters?: any) => [...qualityKeys.all, 'inspections', filters] as const,
  inspection: (id: string) => [...qualityKeys.all, 'inspection', id] as const,
  ncrs: (filters?: any) => [...qualityKeys.all, 'ncrs', filters] as const,
  ncr: (id: string) => [...qualityKeys.all, 'ncr', id] as const,
  capas: (filters?: any) => [...qualityKeys.all, 'capas', filters] as const,
  capa: (id: string) => [...qualityKeys.all, 'capa', id] as const,
};

// Quality Plans
export function useQualityPlans(filters?: { project_id?: string; status?: string }) {
  return useQuery({
    queryKey: qualityKeys.qualityPlans(filters),
    queryFn: () => qualityService.getQualityPlans(filters),
    staleTime: 5 * 60 * 1000,
  });
}

export function useQualityPlan(id: string) {
  return useQuery({
    queryKey: qualityKeys.qualityPlan(id),
    queryFn: () => qualityService.getQualityPlanById(id),
    enabled: !!id,
  });
}

export function useCreateQualityPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<QualityPlan>) => qualityService.createQualityPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlans() });
      message.success('Quality plan created successfully');
    },
    onError: () => message.error('Failed to create quality plan'),
  });
}

export function useUpdateQualityPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<QualityPlan> }) =>
      qualityService.updateQualityPlan(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlan(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlans() });
      message.success('Quality plan updated successfully');
    },
    onError: () => message.error('Failed to update quality plan'),
  });
}

export function useApproveQualityPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      qualityService.approveQualityPlan(id, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlan(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlans() });
      message.success('Quality plan approved successfully');
    },
    onError: () => message.error('Failed to approve quality plan'),
  });
}

// Work Order Integration Hooks
export function useCreateQualityPlanFromWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workOrderId, data }: { workOrderId: string; data?: Partial<QualityPlan> }) =>
      qualityService.createQualityPlanFromWorkOrder(workOrderId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlans() });
      queryClient.invalidateQueries({ queryKey: ['work-orders', variables.workOrderId] });
      message.success('Quality plan created from work order successfully');
    },
    onError: () => message.error('Failed to create quality plan from work order'),
  });
}

export function useQualityPlanByWorkOrder(
  workOrderId: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['quality-plan-by-work-order', workOrderId],
    queryFn: () => qualityService.getQualityPlanByWorkOrder(workOrderId),
    enabled: !!workOrderId && (options?.enabled ?? true),
    staleTime: 2 * 60 * 1000,
  });
}

/** Pending inspection items from all quality plans (for Inspections menu) */
export function usePendingQualityPlanItems() {
  return useQuery({
    queryKey: qualityKeys.pendingQualityPlanItems(),
    queryFn: () => qualityService.getPendingQualityPlanItems(),
    staleTime: 2 * 60 * 1000,
  });
}

// Approval Workflow Hooks
export function useSubmitQualityPlanForApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => qualityService.submitForApproval(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlan(data.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlans() });
      message.success('Quality plan submitted for approval successfully');
    },
    onError: () => message.error('Failed to submit quality plan for approval'),
  });
}

export function useRejectQualityPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: RejectionPayload }) =>
      qualityService.rejectQualityPlan(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlan(data.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlans() });
      message.success('Quality plan rejected');
    },
    onError: () => message.error('Failed to reject quality plan'),
  });
}

// Quality Plan Items
export function useQualityPlanItems(planId: string) {
  return useQuery({
    queryKey: qualityKeys.qualityPlanItems(planId),
    queryFn: () => qualityService.getQualityPlanItems(planId),
    enabled: !!planId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCreateQualityPlanItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, data }: { planId: string; data: Partial<QualityPlanItem> }) =>
      qualityService.createQualityPlanItem(planId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlanItems(variables.planId) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlan(variables.planId) });
      message.success('Inspection item created successfully');
    },
    onError: () => message.error('Failed to create inspection item'),
  });
}

export function useUpdateQualityPlanItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, itemId, data }: { planId: string; itemId: string; data: Partial<QualityPlanItem> }) =>
      qualityService.updateQualityPlanItem(planId, itemId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlanItems(variables.planId) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlan(variables.planId) });
      message.success('Inspection item updated successfully');
    },
    onError: () => message.error('Failed to update inspection item'),
  });
}

export function useDeleteQualityPlanItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, itemId }: { planId: string; itemId: string }) =>
      qualityService.deleteQualityPlanItem(planId, itemId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlanItems(variables.planId) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.qualityPlan(variables.planId) });
      message.success('Inspection item deleted successfully');
    },
    onError: () => message.error('Failed to delete inspection item'),
  });
}

// Inspections
export function useInspections(filters?: {
  project_id?: string;
  status?: string;
  result?: string;
  inspection_category?: string;
  source_document_type?: string;
}) {
  return useQuery({
    queryKey: qualityKeys.inspections(filters),
    queryFn: () => qualityService.getInspections(filters),
    staleTime: 2 * 60 * 1000,
  });
}

export function useInspection(id: string) {
  return useQuery({
    queryKey: qualityKeys.inspection(id),
    queryFn: () => qualityService.getInspectionById(id),
    enabled: !!id,
  });
}

export function useCreateInspection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<InspectionRecord>) => qualityService.createInspection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.inspections() });
      message.success('Inspection record created successfully');
    },
    onError: () => message.error('Failed to create inspection record'),
  });
}

export function useUpdateInspection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InspectionRecord> }) =>
      qualityService.updateInspection(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.inspection(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.inspections() });
      message.success('Inspection updated successfully');
    },
    onError: () => message.error('Failed to update inspection'),
  });
}

export function useCompleteInspection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, result, findings }: { id: string; result: string; findings?: string }) =>
      qualityService.completeInspection(id, result, findings),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.inspection(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.inspections() });
      message.success('Inspection completed successfully');
    },
    onError: () => message.error('Failed to complete inspection'),
  });
}

// NCRs
export function useNCRs(filters?: { project_id?: string; status?: string; severity?: string }) {
  return useQuery({
    queryKey: qualityKeys.ncrs(filters),
    queryFn: () => qualityService.getNCRs(filters),
    staleTime: 2 * 60 * 1000,
  });
}

export function useNCR(id: string) {
  return useQuery({
    queryKey: qualityKeys.ncr(id),
    queryFn: () => qualityService.getNCRById(id),
    enabled: !!id,
  });
}

export function useCreateNCR() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<NCRRecord>) => qualityService.createNCR(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncrs() });
      message.success('NCR created successfully');
    },
    onError: (error: any) => {
      const detail =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      message.error(detail ? `Failed to create NCR: ${detail}` : 'Failed to create NCR');
    },
  });
}

export function useUpdateNCR() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<NCRRecord> }) =>
      qualityService.updateNCR(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncrs() });
      message.success('NCR updated successfully');
    },
    onError: () => message.error('Failed to update NCR'),
  });
}

export function useCloseNCR() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) => qualityService.closeNCR(id, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncrs() });
      message.success('NCR closed successfully');
    },
    onError: () => message.error('Failed to close NCR'),
  });
}

export function useUpdateNCRStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => qualityService.updateNCRStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncrs() });
      message.success('NCR status updated successfully');
    },
    onError: () => message.error('Failed to update NCR status'),
  });
}

export function useSubmitNCRForApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => qualityService.submitNCRForApproval(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncrs() });
      message.success('NCR submitted for approval');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to submit NCR for approval'),
  });
}

export function useAddRootCauseAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { root_cause_analysis: string; root_cause_method?: string; root_cause_verified?: boolean } }) =>
      qualityService.addRootCauseAnalysis(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncrs() });
      message.success('Root cause analysis added successfully');
    },
    onError: () => message.error('Failed to add root cause analysis'),
  });
}

export function useAddRootCause() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ncrId,
      data,
    }: {
      ncrId: string;
      data: Parameters<typeof qualityService.addRootCause>[1];
    }) => qualityService.addRootCause(ncrId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(variables.ncrId) });
      message.success('Root cause analysis added');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to add root cause'),
  });
}

export function useUpdateRootCause() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ncrId,
      rcaId,
      data,
    }: {
      ncrId: string;
      rcaId: string;
      data: Parameters<typeof qualityService.updateRootCause>[2];
    }) => qualityService.updateRootCause(ncrId, rcaId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(variables.ncrId) });
      message.success('Root cause analysis updated');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to update root cause'),
  });
}

export function useDeleteRootCause() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ncrId, rcaId }: { ncrId: string; rcaId: string }) =>
      qualityService.deleteRootCause(ncrId, rcaId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(variables.ncrId) });
      message.success('Root cause analysis deleted');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to delete root cause'),
  });
}

export function useNcrCapas(ncrId: string) {
  return useQuery({
    queryKey: [...qualityKeys.ncr(ncrId), 'capas'],
    queryFn: () => qualityService.listNcrCapas(ncrId),
    enabled: !!ncrId,
  });
}

export function useLinkCAPA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, capaId }: { id: string; capaId: string }) =>
      qualityService.linkCAPA(id, capaId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(variables.id) });
      queryClient.invalidateQueries({ queryKey: [...qualityKeys.ncr(variables.id), 'capas'] });
      queryClient.invalidateQueries({ queryKey: qualityKeys.capas() });
      message.success('CAPA linked to NCR');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to link CAPA'),
  });
}

export function useSetDisposition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof qualityService.setDisposition>[1];
    }) => qualityService.setDisposition(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncrs() });
      message.success('Disposition set successfully');
    },
    onError: () => message.error('Failed to set disposition'),
  });
}

export function useRequireCAPA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, capaRequired }: { id: string; capaRequired: boolean }) =>
      qualityService.requireCAPA(id, capaRequired),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncrs() });
      message.success('CAPA requirement updated successfully');
    },
    onError: () => message.error('Failed to update CAPA requirement'),
  });
}

export function useVerifyEffectiveness() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof qualityService.verifyEffectiveness>[1];
    }) => qualityService.verifyEffectiveness(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.ncrs() });
      message.success('Effectiveness verified successfully');
    },
    onError: () => message.error('Failed to verify effectiveness'),
  });
}

// CAPAs
export function useCAPAs(filters?: { project_id?: string; status?: string; type?: string }) {
  return useQuery({
    queryKey: qualityKeys.capas(filters),
    queryFn: () => qualityService.getCAPAs(filters),
    staleTime: 2 * 60 * 1000,
  });
}

export function useCAPA(id: string) {
  return useQuery({
    queryKey: qualityKeys.capa(id),
    queryFn: () => qualityService.getCAPAById(id),
    enabled: !!id,
  });
}

export function useCreateCAPA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CAPARecord>) => qualityService.createCAPA(data),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.capas() });
      if (created?.ncr_id) {
        queryClient.invalidateQueries({ queryKey: qualityKeys.ncr(created.ncr_id) });
        queryClient.invalidateQueries({
          queryKey: [...qualityKeys.ncr(created.ncr_id), 'capas'],
        });
      }
      message.success('CAPA created successfully');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to create CAPA'),
  });
}

export function useUpdateCAPA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CAPARecord> }) =>
      qualityService.updateCAPA(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.capa(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.capas() });
      message.success('CAPA updated successfully');
    },
    onError: () => message.error('Failed to update CAPA'),
  });
}

export function useCloseCAPA() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) => qualityService.closeCAPA(id, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.capa(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.capas() });
      message.success('CAPA closed successfully');
    },
    onError: () => message.error('Failed to close CAPA'),
  });
}

export function useUpdateCAPAStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      qualityService.updateCAPAStatus(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.capa(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.capas() });
      message.success('CAPA status updated');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to update status'),
  });
}

export function useSubmitCAPAForApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => qualityService.submitCAPAForApproval(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.capa(id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.capas() });
      message.success('CAPA submitted for approval');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to submit CAPA for approval'),
  });
}

export function useVerifyCAPAEffectiveness() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof qualityService.verifyCAPAEffectiveness>[1];
    }) => qualityService.verifyCAPAEffectiveness(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: qualityKeys.capa(variables.id) });
      queryClient.invalidateQueries({ queryKey: qualityKeys.capas() });
      message.success('Effectiveness recorded');
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Failed to verify effectiveness'),
  });
}

